// The questionnaire wizard UI (v3 — the definition is questionnaire/questions.js).
import { useState, useEffect, useRef } from "react";
import { ChevronRight, Check } from "lucide-react";
import { useLang } from "../../i18n.jsx";
import { api } from "../api.js";
import { BACK_PRIORITY, useHostBackButton } from "../backbutton.js";
import { haptic, useClosingConfirmation } from "../host.js";
import { FluentEmoji } from "../emoji.jsx";
import { toFa } from "../format.js";
import {
  DEV_FLAG_IDS, DONT_KNOW, FEAR_ITEMS, FREQ_OPTIONS, IMPACT_OPTIONS, LIFE_EVENT_GROUPS, NO_PRIORITY,
  PARENT_FREQ_OPTIONS, Q_SECTIONS, Q_VERSION, isVisible, priorityCandidates,
} from "../questionnaire/questions.js";
import { C, FONT } from "../../design-system.js";
import { AdSlot } from "../ui.jsx";

// A diagnosis the parent already has makes the «talk to a specialist» note redundant.
const NO_DX = ["هیچ‌کدام", "ترجیح می‌دهم نگویم"];

// `child` = {name, age} — the age picks which questions are shown.
// `ads` = a free family — Adly's card is pinned just ABOVE the «قبلی / بعدی» bar.
export function QuestionnaireScreen({ childId, child, onBack, onDone, ads = false }) {
  const { t, dir } = useLang();
  const [section, setSection] = useState(0);
  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const scrollRef = useRef(null);
  const age = Number(child?.age) || null;
  const name = child?.name || t("کودک");
  const tn = (s) => t(s, { name });

  // Every new section must start at the top. The page scrolls on the window
  // (the screen div grows past the viewport), so reset both window and the div
  // AFTER the section's content renders.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    if (scrollRef.current) scrollRef.current.scrollTo({ top: 0 });
  }, [section]);

  const visible = (q) => isVisible(q, answers, age);
  const currentSection = Q_SECTIONS[section];
  const totalSections = Q_SECTIONS.length;

  function setAnswer(id, value) {
    setAnswers(prev => ({ ...prev, [id]: value }));
    setErrors(prev => { const n = { ...prev }; delete n[id]; return n; });
  }

  function toggleMulti(id, option, max, exclusive) {
    setAnswers(prev => {
      const curr = prev[id] || [];
      if (curr.includes(option)) {
        return { ...prev, [id]: curr.filter(x => x !== option) };
      }
      // An `exclusive` option (e.g. «هیچ‌کدام») clears the rest and stands alone;
      // picking any other option removes the exclusive one.
      if (exclusive && option === exclusive) return { ...prev, [id]: [option] };
      const base = exclusive ? curr.filter(x => x !== exclusive) : curr;
      if (max && base.length >= max) return prev; // cap at max
      return { ...prev, [id]: [...base, option] };
    });
    setErrors(prev => { const n = { ...prev }; delete n[id]; return n; });
  }

  function setFear(id, value) {
    setAnswers(prev => {
      const next = { ...prev };
      if (value === null) delete next[id]; else next[id] = value;
      return next;
    });
    setErrors(prev => { const n = { ...prev }; delete n.fear_gate; return n; });
  }

  function setEvent(id, value) {
    setAnswers(prev => {
      const ev = { ...(prev.life_events || {}) };
      if (value === null) delete ev[id]; else ev[id] = value;
      return { ...prev, life_events: ev };
    });
    setErrors(prev => { const n = { ...prev }; delete n.life_events; return n; });
  }

  function validateSection(sec) {
    const newErrors = {};
    for (const q of Q_SECTIONS[sec].questions) {
      if (q.type === "header" || q.type === "devnote" || !visible(q) || q.optional) continue;
      const val = answers[q.id];
      if (q.type === "rank") {
        // Required only when there is something to choose from.
        if (priorityCandidates(answers).length > 0 && !(val || []).length) newErrors[q.id] = true;
        continue;
      }
      if (q.type === "fearlist") {
        if (val !== true && val !== false) newErrors[q.id] = true;
        else if (val === true && !FEAR_ITEMS.some(f => answers[f.id]?.has)) newErrors[q.id] = true;
        continue;
      }
      if (q.type === "lifeevents") {
        const ev = answers.life_events;
        if (!ev || (!Object.keys(ev).length && !answers.life_none)) newErrors[q.id] = true;
        else if (Object.values(ev).some(e => !e.impact || e.ongoing === undefined)) newErrors[q.id] = true;
        continue;
      }
      if (q.type === "text" && q.required === false) continue;
      if (q.type === "yesno") { if (val !== true && val !== false) newErrors[q.id] = true; continue; }
      if (val === undefined || val === null || val === "") {
        newErrors[q.id] = true;
      } else if (q.type === "multi" && Array.isArray(val) && val.length === 0) {
        newErrors[q.id] = true;
      }
    }
    return newErrors;
  }

  function goNext() {
    const errs = validateSection(section);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setSection(s => s + 1);
  }

  function goPrev() {
    setErrors({});
    setSection(s => s - 1);
  }

  // The host back arrow must do what the in-page «بخش قبلی» does — step back
  // through the wizard, and only leave the questionnaire from the first
  // section. Registered at `inner` depth so it outranks the shell's goHome.
  useHostBackButton(section === 0 ? onBack : goPrev, { priority: BACK_PRIORITY.inner });

  // The answers live only in component state until the last screen, so a stray
  // close/back-swipe used to silently discard all of it. Ask the host to confirm
  // while the wizard is open — and only while it is open, or every later exit
  // from the app would prompt for nothing.
  useClosingConfirmation(true);

  async function handleSubmit() {
    const errs = validateSection(section);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    if (submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      // Only what this child was actually asked — a hidden question (wrong age,
      // or made irrelevant by a later answer) never reaches the server.
      const flat = { _v: Q_VERSION };
      for (const sec of Q_SECTIONS) {
        for (const q of sec.questions) {
          if (q.type === "header" || q.type === "devnote" || !visible(q)) continue;
          const val = answers[q.id];
          if (q.type === "fearlist") {
            flat.fear_gate = val === true;
            if (val === true) for (const f of FEAR_ITEMS) if (answers[f.id]?.has) flat[f.id] = answers[f.id];
            continue;
          }
          if (q.type === "lifeevents") { flat.life_events = answers.life_events || {}; continue; }
          if (q.type === "rank") {
            const picks = val || [];
            if (picks.length) { flat.q_priority = picks[0]; flat.q_priority_more = picks.slice(1); }
            continue;
          }
          if (val === undefined || val === null || val === "") continue;
          if (Array.isArray(val) && !val.length) continue;
          flat[q.id] = val;
        }
      }
      await api("/questionnaire", { method: "POST", body: JSON.stringify({ child_id: childId, answers: flat }) });
      onDone();
    } catch (e) {
      setSubmitError(e.message || t("خطا در ارسال پرسشنامه"));
    } finally {
      setSubmitting(false);
    }
  }

  const progressPct = ((section + 1) / totalSections) * 100;

  // ── Render helpers ──
  const card = (id, pad = "16px 14px") => ({ background: C.bgCard, border: `1px solid ${errors[id] ? C.red : C.border}`, borderRadius: 18, padding: pad, marginBottom: 14, transition: "border-color 0.2s" });
  const errLine = (id, msg = "لطفاً این سوال را پاسخ دهید") => errors[id] && <p style={{ color: C.red, fontSize: 12, margin: "8px 0 0" }}>{t(msg)}</p>;
  const mark = (q) => q.optional
    ? <span style={{ color: C.textDim, fontSize: 12, fontWeight: 400, marginRight: 6 }}>{t("(اختیاری)")}</span>
    : <span style={{ color: C.red, marginRight: 4 }}>*</span>;
  const Desc = ({ q }) => q.desc ? <p style={{ color: C.textMid, fontSize: 13, margin: "0 0 12px", lineHeight: 1.8 }}>{tn(q.desc)}</p> : null;
  const pill = (selected, color = C.gold) => ({ background: selected ? `${color}22` : C.bg, border: `1.5px solid ${selected ? color : C.border}`, cursor: "pointer", fontFamily: FONT, transition: "all 0.15s" });

  function renderFrequency(q) {
    const val = answers[q.id];
    const opts = q.scale === "parent" ? PARENT_FREQ_OPTIONS : [...FREQ_OPTIONS, DONT_KNOW];
    return (
      <div style={{ ...card(q.id, "14px 14px 10px"), borderRadius: 16, marginBottom: 10 }}>
        <p style={{ color: C.cream, fontSize: 14, fontWeight: 600, margin: "0 0 10px", lineHeight: 1.7 }}>{tn(q.label)}{q.optional && mark(q)}</p>
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {opts.map(opt => {
            const selected = val === opt.value;
            const dk = opt.value === DONT_KNOW.value && q.scale !== "parent";
            return (
              <button key={opt.value} onClick={() => setAnswer(q.id, opt.value)} className="press"
                style={{ ...pill(selected, dk ? C.textMid : C.gold), flex: dk ? "0.8 1 0" : "1 1 0", minWidth: 0, borderRadius: 10, padding: "8px 4px", textAlign: "center" }}>
                <span style={{ color: selected ? (dk ? C.cream : C.gold) : (dk ? C.textMid : C.creamDim), fontSize: 11, fontWeight: selected ? 700 : 400, lineHeight: 1.4, display: "block" }}>{t(opt.label)}</span>
              </button>
            );
          })}
        </div>
        {errors[q.id] && <p style={{ color: C.red, fontSize: 12, margin: "6px 0 0" }}>{t("لطفاً این سوال را پاسخ دهید")}</p>}
      </div>
    );
  }

  function renderSingle(q) {
    const val = answers[q.id];
    return (
      <div style={card(q.id)}>
        <p style={{ color: C.cream, fontSize: 15, fontWeight: 700, margin: "0 0 4px", lineHeight: 1.7 }}>{tn(q.label)} {mark(q)}</p>
        <Desc q={q} />
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: q.desc ? 0 : 8 }}>
          {q.options.map(opt => {
            const selected = val === opt;
            return (
              <button key={opt} onClick={() => setAnswer(q.id, opt)} className="press"
                style={{ ...pill(selected), background: selected ? `${C.gold}18` : C.bg, borderRadius: 12, padding: "12px 14px", display: "flex", alignItems: "center", gap: 10, textAlign: "right" }}>
                <div style={{ width: 22, height: 22, borderRadius: "50%", border: `2px solid ${selected ? C.gold : C.textDim}`, background: selected ? C.gold : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all 0.15s" }}>
                  {selected && <Check size={13} color={C.bgDeep} strokeWidth={3} />}
                </div>
                <span style={{ color: selected ? C.cream : C.creamDim, fontSize: 14, fontWeight: selected ? 600 : 400, lineHeight: 1.6, flex: 1 }}>{t(opt)}</span>
              </button>
            );
          })}
        </div>
        {errLine(q.id)}
      </div>
    );
  }

  function renderText(q) {
    return (
      <div style={card(q.id)}>
        <label style={{ color: C.cream, fontSize: 15, fontWeight: 700, display: "block", marginBottom: 4, lineHeight: 1.7 }}>
          {tn(q.label)}
          {q.required === false
            ? <span style={{ color: C.textDim, fontSize: 12, fontWeight: 400, marginRight: 6 }}>{t("(اختیاری)")}</span>
            : <span style={{ color: C.red, marginRight: 4 }}>*</span>}
        </label>
        {q.desc && <p style={{ color: C.textMid, fontSize: 13, margin: "0 0 10px", lineHeight: 1.8 }}>{tn(q.desc)}</p>}
        <textarea
          value={answers[q.id] || ""}
          onChange={e => { if (e.target.value.length <= (q.maxLen || 300)) setAnswer(q.id, e.target.value); }}
          placeholder={t("اینجا بنویسید...")}
          rows={(q.maxLen || 300) <= 80 ? 1 : 3}
          style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12, padding: "12px 14px", color: C.cream, fontFamily: FONT, fontSize: 14, lineHeight: 1.8, outline: "none", resize: "vertical", boxSizing: "border-box" }}
        />
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 6 }}>
          <span style={{ color: (answers[q.id] || "").length >= (q.maxLen || 300) ? C.red : C.textDim, fontSize: 12 }}>
            {toFa((answers[q.id] || "").length)} / {toFa(q.maxLen || 300)}
          </span>
        </div>
        {errLine(q.id)}
      </div>
    );
  }

  function YesNo({ value, onChange }) {
    return (
      <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
        {[true, false].map(v => (
          <button key={String(v)} onClick={() => onChange(v)} className="press"
            style={{ ...pill(value === v, v ? C.teal : C.red), borderRadius: 10, padding: "6px 14px" }}>
            <span style={{ color: value === v ? (v ? C.teal : C.red) : C.creamDim, fontSize: 13, fontWeight: value === v ? 700 : 400 }}>{v ? t("بله") : t("خیر")}</span>
          </button>
        ))}
      </div>
    );
  }

  function renderYesNo(q) {
    return (
      <div style={{ ...card(q.id, "14px 14px"), borderRadius: 16, marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
          <p style={{ color: C.cream, fontSize: 14, fontWeight: 600, margin: 0, lineHeight: 1.7, flex: 1 }}>{tn(q.label)}{q.optional && mark(q)}</p>
          <YesNo value={answers[q.id]} onChange={v => setAnswer(q.id, v)} />
        </div>
        {errLine(q.id)}
      </div>
    );
  }

  function renderMulti(q) {
    const val = answers[q.id] || [];
    return (
      <div style={card(q.id)}>
        <p style={{ color: C.cream, fontSize: 15, fontWeight: 700, margin: "0 0 4px", lineHeight: 1.7 }}>{tn(q.label)} {mark(q)}</p>
        <Desc q={q} />
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: q.desc ? 0 : 8 }}>
          {q.options.map(opt => {
            const selected = val.includes(opt);
            const atMax = q.max && val.length >= q.max && !selected;
            return (
              <button key={opt} onClick={() => toggleMulti(q.id, opt, q.max, q.exclusive)} className="press"
                style={{ ...pill(selected), borderRadius: 20, padding: "8px 16px", cursor: atMax ? "default" : "pointer", opacity: atMax ? 0.45 : 1, display: "flex", alignItems: "center", gap: 6 }}>
                {selected && <Check size={13} color={C.gold} strokeWidth={3} />}
                <span style={{ color: selected ? C.gold : C.creamDim, fontSize: 13, fontWeight: selected ? 700 : 400 }}>{t(opt)}</span>
              </button>
            );
          })}
        </div>
        {errLine(q.id)}
      </div>
    );
  }

  // Life events: chips by group; a picked chip opens two short follow-ups —
  // still going on? and how much did it affect the child?
  function renderLifeEvents(q) {
    const ev = answers.life_events || {};
    const none = !!answers.life_none && !Object.keys(ev).length;
    return (
      <div style={card(q.id)}>
        <p style={{ color: C.cream, fontSize: 15, fontWeight: 700, margin: "0 0 4px", lineHeight: 1.7 }}>{tn(q.label)} {mark(q)}</p>
        <Desc q={q} />
        {LIFE_EVENT_GROUPS.map(g => (
          <div key={g.title} style={{ marginBottom: 12 }}>
            <p style={{ color: C.goldLight, fontSize: 13, fontWeight: 700, margin: "0 0 8px" }}>{t(g.title)}</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {g.items.map(item => {
                const cur = ev[item.id];
                return (
                  <div key={item.id} style={{ ...pill(!!cur), cursor: "default", borderRadius: 12, padding: cur ? "10px 12px" : 0 }}>
                    <button onClick={() => { setEvent(item.id, cur ? null : {}); setAnswer("life_none", false); }} className="press"
                      style={{ background: "none", border: "none", padding: cur ? 0 : "10px 12px", width: "100%", display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontFamily: FONT, textAlign: "right" }}>
                      <div style={{ width: 20, height: 20, borderRadius: 6, border: `2px solid ${cur ? C.gold : C.textDim}`, background: cur ? C.gold : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        {cur && <Check size={12} color={C.bgDeep} strokeWidth={3} />}
                      </div>
                      <span style={{ color: cur ? C.cream : C.creamDim, fontSize: 13.5, fontWeight: cur ? 600 : 400, lineHeight: 1.6 }}>{t(item.label)}</span>
                    </button>
                    {cur && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 10, alignItems: "center" }}>
                        <div style={{ display: "flex", gap: 6 }}>
                          {[["هنوز ادامه دارد", true], ["تمام شده", false]].map(([lbl, v]) => (
                            <button key={lbl} onClick={() => setEvent(item.id, { ...cur, ongoing: v })} className="press"
                              style={{ ...pill(cur.ongoing === v, C.teal), borderRadius: 10, padding: "5px 10px" }}>
                              <span style={{ color: cur.ongoing === v ? C.teal : C.creamDim, fontSize: 12 }}>{t(lbl)}</span>
                            </button>
                          ))}
                        </div>
                        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                          <span style={{ color: C.textMid, fontSize: 12 }}>{t("اثرش:")}</span>
                          {IMPACT_OPTIONS.map(o => (
                            <button key={o.value} onClick={() => setEvent(item.id, { ...cur, impact: o.value })} className="press"
                              style={{ ...pill(cur.impact === o.value), borderRadius: 10, padding: "5px 10px" }}>
                              <span style={{ color: cur.impact === o.value ? C.gold : C.creamDim, fontSize: 12 }}>{t(o.label)}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        <button onClick={() => { setAnswers(p => ({ ...p, life_events: {}, life_none: true })); setErrors(p => { const n = { ...p }; delete n.life_events; return n; }); }} className="press"
          style={{ ...pill(none, C.teal), borderRadius: 20, padding: "8px 16px", display: "flex", alignItems: "center", gap: 6 }}>
          {none && <Check size={13} color={C.teal} strokeWidth={3} />}
          <span style={{ color: none ? C.teal : C.creamDim, fontSize: 13, fontWeight: none ? 700 : 400 }}>{t("هیچ‌کدام / تغییر خاصی رخ نداده است")}</span>
        </button>
        {errLine(q.id, "برای هر مورد، ادامه داشتن و میزان اثرش را هم انتخاب کنید")}
      </div>
    );
  }

  // Fears: a yes/no gate, then each fear as a chip; a picked one asks its severity.
  function renderFearList(q) {
    const gate = answers.fear_gate;
    return (
      <div style={card(q.id)}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
          <p style={{ color: C.cream, fontSize: 15, fontWeight: 700, margin: 0, lineHeight: 1.7, flex: 1 }}>{tn(q.label)} {mark(q)}</p>
          <YesNo value={gate} onChange={v => setAnswer("fear_gate", v)} />
        </div>
        {gate === true && (
          <>
            <Desc q={q} />
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {FEAR_ITEMS.map(f => {
                const cur = answers[f.id];
                return (
                  <div key={f.id} style={{ ...pill(!!cur?.has, C.red), cursor: "default", borderRadius: 12, padding: "0 12px" }}>
                    <button onClick={() => setFear(f.id, cur?.has ? null : { has: true, severity: 2 })} className="press"
                      style={{ background: "none", border: "none", padding: "10px 0", width: "100%", display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontFamily: FONT, textAlign: "right" }}>
                      <div style={{ width: 20, height: 20, borderRadius: 6, border: `2px solid ${cur?.has ? C.red : C.textDim}`, background: cur?.has ? C.red : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        {cur?.has && <Check size={12} color={C.bgDeep} strokeWidth={3} />}
                      </div>
                      <span style={{ color: cur?.has ? C.cream : C.creamDim, fontSize: 13.5, fontWeight: cur?.has ? 600 : 400 }}>{t(f.label)}</span>
                    </button>
                    {cur?.has && (
                      <div style={{ display: "flex", gap: 6, alignItems: "center", paddingBottom: 10 }}>
                        <span style={{ color: C.textMid, fontSize: 12 }}>{t("شدت ترس:")}</span>
                        {[1, 2, 3, 4].map(s => (
                          <button key={s} onClick={() => setFear(f.id, { has: true, severity: s })} className="press"
                            style={{ ...pill(cur.severity === s), flex: 1, borderRadius: 8, padding: "5px 0" }}>
                            <span style={{ color: cur.severity === s ? C.gold : C.creamDim, fontSize: 13, fontWeight: cur.severity === s ? 800 : 400 }}>{toFa(s)}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
        {errLine(q.id, gate === true ? "دست‌کم یک ترس را انتخاب کنید" : "لطفاً این سوال را پاسخ دهید")}
      </div>
    );
  }

  // «اولویت‌ها»: pick up to `max` topics IN ORDER — the first is the first season.
  function renderRank(q) {
    const options = priorityCandidates(answers);
    const picks = answers[q.id] || [];
    // Functional update: two quick taps must not both read the same old picks.
    const toggle = (label) => {
      setAnswers(prev => {
        const cur = prev[q.id] || [];
        let next;
        if (label === NO_PRIORITY) next = cur[0] === NO_PRIORITY ? [] : [NO_PRIORITY];
        else {
          const base = cur.filter(p => p !== NO_PRIORITY);
          if (base.includes(label)) next = base.filter(p => p !== label);
          else if (base.length >= q.max) return prev;
          else next = [...base, label];
        }
        return { ...prev, [q.id]: next };
      });
      setErrors(prev => { const n = { ...prev }; delete n[q.id]; return n; });
    };
    if (options.length === 0) {
      return (
        <div style={{ background: `${C.teal}12`, border: `1px solid ${C.teal}44`, borderRadius: 16, padding: "16px 16px", marginBottom: 12 }}>
          <p style={{ color: C.teal, fontSize: 14, fontWeight: 700, margin: "0 0 6px" }}>{t("چالش پررنگی ثبت نشد ✨")}</p>
          <p style={{ color: C.creamDim, fontSize: 13, lineHeight: 1.9, margin: 0 }}>
            {t("بر اساس پاسخ‌های شما، کودک در حال حاضر چالشِ برجسته‌ای ندارد. داستان‌ها بر پرورشِ توانمندی‌ها و ارزش‌های مورد نظرِ شما تمرکز می‌کنند.")}
          </p>
        </div>
      );
    }
    return (
      <div style={card(q.id)}>
        <p style={{ color: C.cream, fontSize: 15, fontWeight: 700, margin: "0 0 4px", lineHeight: 1.7 }}>{tn(q.label)} {mark(q)}</p>
        <p style={{ color: C.textMid, fontSize: 13, margin: "0 0 12px", lineHeight: 1.8 }}>{t("موارد زیر از پاسخ‌های شما استخراج شده‌اند. به ترتیب اهمیت انتخاب کنید.")}</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {[...options, { id: "none", label: NO_PRIORITY }].map(({ id, label }) => {
            const at = picks.indexOf(label);
            const selected = at >= 0;
            const full = !selected && label !== NO_PRIORITY && picks.filter(p => p !== NO_PRIORITY).length >= q.max;
            return (
              <button key={label} onClick={() => toggle(label)} className="press"
                style={{ ...pill(selected), background: selected ? `${C.gold}18` : C.bg, borderRadius: 12, padding: "12px 14px", display: "flex", alignItems: "center", gap: 10, textAlign: "right", opacity: full ? 0.45 : 1 }}>
                <div style={{ width: 24, height: 24, borderRadius: "50%", border: `2px solid ${selected ? C.gold : C.textDim}`, background: selected ? C.gold : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: C.bgDeep, fontSize: 13, fontWeight: 800 }}>
                  {selected && (label === NO_PRIORITY ? <Check size={13} color={C.bgDeep} strokeWidth={3} /> : toFa(at + 1))}
                </div>
                <span style={{ color: selected ? C.cream : C.creamDim, fontSize: 14, fontWeight: selected ? 600 : 400, lineHeight: 1.6, flex: 1 }}>
                  {id.startsWith("val:") ? t("پرورش «{v}»", { v: t(id.slice(4)) }) : t(label)}
                </span>
              </button>
            );
          })}
        </div>
        {errLine(q.id, "لطفاً دست‌کم یک مورد را انتخاب کنید")}
      </div>
    );
  }

  // Gentle, non-diagnostic advisory shown only when several developmental-screening
  // items are marked high — and not to a family that already has a diagnosis.
  function renderDevNote() {
    const dx = (answers.hlt_dx || []).filter(x => !NO_DX.includes(x));
    const shownDev = Q_SECTIONS.flatMap(s => s.questions).filter(q => DEV_FLAG_IDS.includes(q.id) && visible(q));
    const count = shownDev.filter(q => typeof answers[q.id] === "number" && answers[q.id] >= 3).length;
    if (count < 3 || dx.length) return null;
    return (
      <div style={{ background: `${C.purple}12`, border: `1px solid ${C.purple}44`, borderRadius: 16, padding: "16px 16px", marginTop: 6, marginBottom: 8 }}>
        <p style={{ color: C.purple, fontSize: 14, fontWeight: 700, margin: "0 0 6px", display: "flex", alignItems: "center", gap: 6 }}>
          <FluentEmoji size={18}>💜</FluentEmoji> {t("یک یادداشت دوستانه")}
        </p>
        <p style={{ color: C.creamDim, fontSize: 13, lineHeight: 2, margin: 0 }}>
          {t("چند مورد از این نشانه‌ها را هم‌زمان علامت زده‌اید. این پرسشنامه تشخیصی نیست، اما در این حوزه، مشورتِ زودهنگام با پزشک کودک یا متخصص رشد بسیار ارزشمند است. پیشنهاد می‌کنیم مستقل از داستان‌ها، این موضوع را با یک متخصص در میان بگذارید. 🌱")}
        </p>
      </div>
    );
  }

  function renderQuestion(q, qi) {
    if (q.type === "devnote") return <div key={q.id}>{renderDevNote()}</div>;
    if (!visible(q)) return null;
    if (q.type === "header") {
      return (
        <div key={q.id} style={{ margin: qi === 0 ? "0 0 12px" : "22px 0 12px", padding: "0 2px" }}>
          <p style={{ color: C.goldLight, fontSize: 15, fontWeight: 800, margin: 0, lineHeight: 1.6 }}>{tn(q.label)}</p>
          {q.desc && <p style={{ color: C.textMid, fontSize: 13, margin: "6px 0 0", lineHeight: 1.8 }}>{tn(q.desc)}</p>}
        </div>
      );
    }
    const render = {
      frequency: renderFrequency, single: renderSingle, text: renderText, yesno: renderYesNo, multi: renderMulti,
      lifeevents: renderLifeEvents, fearlist: renderFearList, rank: renderRank,
    }[q.type];
    return render ? <div key={q.id}>{render(q)}</div> : null;
  }

  return (
    <div ref={scrollRef} style={{ minHeight: "100vh", background: C.bg, fontFamily: FONT, display: "flex", flexDirection: "column", overflowY: "auto" }}>
      {/* Header */}
      <div style={{ background: C.bgDeep, padding: "16px 20px 20px", borderBottom: `1px solid ${C.border}`, position: "sticky", top: 0, zIndex: 10 }}>
        <button onClick={section === 0 ? onBack : goPrev} className="press" style={{ background: "none", border: "none", color: C.gold, fontSize: 15, cursor: "pointer", fontFamily: FONT, padding: 0, marginBottom: 14, display: "flex", alignItems: "center", gap: 4 }}>
          <ChevronRight size={18} style={{ transform: dir === "ltr" ? "rotate(180deg)" : "none" }} /> {section === 0 ? t("بازگشت") : t("بخش قبلی")}
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <FluentEmoji size={28}>{currentSection.icon}</FluentEmoji>
          <div>
            <h2 style={{ color: C.cream, fontSize: 18, fontWeight: 800, margin: 0 }}>{tn(currentSection.title)}</h2>
            <p style={{ color: C.textMid, fontSize: 13, margin: "2px 0 0" }}>{t("بخش {a} از {b}", { a: toFa(section + 1), b: toFa(totalSections) })}</p>
            {currentSection.desc && <p style={{ color: C.creamDim, fontSize: 13, margin: "6px 0 0", lineHeight: 1.7 }}>{tn(currentSection.desc)}</p>}
          </div>
        </div>
        {/* Progress bar */}
        <div style={{ width: "100%", height: 4, background: C.border, borderRadius: 2, overflow: "hidden" }}>
          <div style={{ width: `${progressPct}%`, height: "100%", background: `linear-gradient(90deg, ${C.gold}, ${C.goldLight})`, borderRadius: 2, transition: "width 0.4s ease" }} />
        </div>
      </div>

      {/* Questions */}
      <div style={{ flex: 1, padding: "16px 16px 120px" }}>
        {currentSection.questions.map((q, qi) => renderQuestion(q, qi))}

        {submitError && (
          <div style={{ color: C.red, fontSize: 14, textAlign: "center", marginBottom: 12, animation: "fadeIn 0.2s ease" }}>{submitError}</div>
        )}
      </div>

      <AdSlot show={ads} above={76} />

      {/* Bottom navigation */}
      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: `linear-gradient(transparent, ${C.bgDeep} 20%)`, padding: "40px 20px 24px", display: "flex", gap: 10, zIndex: 10 }}>
        {section > 0 && (
          <button onClick={goPrev} className="press"
            style={{ flex: 1, background: C.bgCard, color: C.cream, border: `1px solid ${C.border}`, borderRadius: 14, padding: "14px 0", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>
            {t("قبلی")}
          </button>
        )}
        {section < totalSections - 1 ? (
          <button onClick={() => { haptic("light"); goNext(); }} className="press"
            style={{ flex: 2, background: `linear-gradient(135deg, ${C.gold}, ${C.goldLight})`, color: C.bgDeep, border: "none", borderRadius: 14, padding: "14px 0", fontSize: 16, fontWeight: 800, cursor: "pointer", fontFamily: FONT }}>
            {t("بعدی")}
          </button>
        ) : (
          <button onClick={handleSubmit} disabled={submitting} className="press celebrate"
            style={{ flex: 2, background: submitting ? C.border : `linear-gradient(135deg, ${C.gold}, ${C.goldLight})`, color: submitting ? C.textDim : C.bgDeep, border: "none", borderRadius: 14, padding: "14px 0", fontSize: 16, fontWeight: 800, cursor: submitting ? "not-allowed" : "pointer", fontFamily: FONT, transition: "all 0.3s" }}>
            {submitting ? t("در حال ارسال...") : t("ارسال پرسشنامه")}
          </button>
        )}
      </div>
    </div>
  );
}
